import { useNavigate } from 'react-router-dom';
import { Button, ServiceCard, SectionHeader, StatCard, TestimonialCard, FeatureItem, CTABanner } from '../components/ui/UI';
import ContactInfo from '../components/ui/ContactInfo';
import { SERVICES, STATS, TESTIMONIALS, WHY_US, COUNTRIES } from '../data/services';
import './Home.css';

export default function Home() {
  const navigate = useNavigate();

  const handleInquire = (serviceTitle) => {
    navigate('/contact', { state: { service: serviceTitle } });
  };

  const featuredServices = SERVICES.slice(0, 6);

  return (
    <div className="page-wrapper">
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="home-hero">
        <div className="home-hero__bg" />
        <div className="container home-hero__inner">
          <div className="home-hero__grid">
            <div className="home-hero__content">
              <span className="section-badge badge-white animate-fade-up">
                Mumbai bombay international worldwide · Worldwide
              </span>
              <h1 className="home-hero__title animate-fade-up delay-1">
                Your Complete<br />
                <span className="home-hero__accent">Service Hub</span><br />
                in Online Mumbai bombay international worldwide
              </h1>
              <p className="home-hero__sub animate-fade-up delay-2">
                From private jet charters and 5-star hotel stays to education,
                job placements, and city tours - 57+ services for foreign visitors,
                students, and locals in Mumbai bombay international worldwide. All under one roof.
                Contact us at +919967765284 (Mobile & WhatsApp), +919136663456 or onlineworldwide11@gmail.com.
              </p>
              <div className="home-hero__btns animate-fade-up delay-3">
                <Button to="/services" size="lg">Explore All Services</Button>
                <Button to="/contact" variant="outline-white" size="lg">Send an Inquiry</Button>
              </div>

              {/* Contact Info Section */}
              <div className="home-hero__contact animate-fade-up delay-4">
                <ContactInfo layout="compact" theme="dark" />
              </div>

              <div className="home-hero__stats animate-fade-up delay-5">
                {STATS.map(s => (
                  <div key={s.label} className="hero-stat-item">
                    <span className="hero-stat-num">{s.num}</span>
                    <span className="hero-stat-label">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="home-hero__visual animate-fade-in delay-2">
              <div className="visual-card visual-card--1">
                <span className="visual-card__icon">✈️</span>
                <div className="visual-card__text">
                  <strong>Private Jets</strong>
                  <span>Worldwide Charter</span>
                </div>
              </div>
              <div className="visual-card visual-card--2">
                <span className="visual-card__icon">🎓</span>
                <div className="visual-card__text">
                  <strong>Education</strong>
                  <span>100+ Courses</span>
                </div>
              </div>
              <div className="visual-card visual-card--3">
                <span className="visual-card__icon">🛂</span>
                <div className="visual-card__text">
                  <strong>Job Visa</strong>
                  <span>20+ Countries</span>
                </div>
              </div>
              <div className="visual-card visual-card--4">
                <span className="visual-card__icon">🏨</span>
                <div className="visual-card__text">
                  <strong>Stay</strong>
                  <span>5-Star Hotels</span>
                </div>
              </div>
              <div className="visual-circle" />
            </div>
          </div>
        </div>

        {/* Decorative globe accent */}
        <div className="home-hero__deco">
          <div className="deco-circle deco-circle--1" />
          <div className="deco-circle deco-circle--2" />
          <div className="deco-circle deco-circle--3" />
        </div>
      </section>

      {/* ── COUNTRIES TICKER ─────────────────────────────────── */}
      <div className="countries-strip">
        <div className="countries-strip__inner">
          <span className="countries-strip__label">Serving clients from:</span>
          <div className="countries-strip__ticker">
            <div className="countries-strip__track">
              {[...COUNTRIES, ...COUNTRIES].map((c, i) => (
                <span key={i} className="countries-strip__item">{c}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── FEATURED SERVICES ────────────────────────────────── */}
      <section className="section-pad">
        <div className="container">
          <SectionHeader
            badge="What We Offer"
            badgeVariant="saffron"
            title="Our Most Popular Services"
            subtitle="Explore a curated selection of our most-requested services in Mumbai bombay international worldwide. From luxury travel to career placements - we've got you covered."
          />
          <div className="home-services-grid">
            {featuredServices.map((s, i) => (
              <div key={s.id} className={`animate-fade-up delay-${Math.min(i + 1, 6)}`}>
                <ServiceCard service={s} onInquire={handleInquire} />
              </div>
            ))}
          </div>
          <div className="home-services__cta">
            <Button to="/services" variant="outline">View All 57+ Services →</Button>
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ────────────────────────────────────── */}
      <section className="section-pad home-why">
        <div className="container">
          <div className="home-why__grid">
            <div className="home-why__left">
              <SectionHeader
                badge="Why Online Worldwide"
                badgeVariant="teal"
                title="One Place for Everything You Need in Mumbai bombay international worldwide"
                subtitle="Whether you're a foreign visitor arriving in Mumbai bombay international worldwide for the first time, a student seeking admission, or a business professional - we simplify everything."
                align="left"
              />
              <Button to="/about">Learn About Us →</Button>
            </div>
            <div className="home-why__features">
              {WHY_US.map((item, i) => (
                <div key={i} className={`animate-fade-up delay-${i + 1}`}>
                  <FeatureItem icon={item.icon} title={item.title} desc={item.desc} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CATEGORY HIGHLIGHTS ──────────────────────────────── */}
      <section className="section-pad home-categories">
        <div className="container">
          <SectionHeader
            badge="By Category"
            title="Services for Every Need"
            subtitle="Browse by what you're looking for in Mumbai bombay international worldwide. Click any category to explore services."
          />
          <div className="home-cat-grid">
            {[
              { icon: '✈️', label: 'Travel & Jets',  desc: 'Private jets, flight tickets, charter services', path: '/services', color: 'saffron' },
              { icon: '🏨', label: 'Accommodation',   desc: 'Hotels, PG, dormitories, offices & shops',      path: '/services', color: 'teal'    },
              { icon: '🎓', label: 'Education',       desc: '100+ courses, admissions, study abroad',         path: '/education', color: 'gold'   },
              { icon: '🛂', label: 'Jobs & Visa',     desc: 'Work abroad, airport jobs, cruise placements',   path: '/jobs',      color: 'navy'   },
              { icon: '🗺️', label: 'Tourism',         desc: 'City tours, bike rides, tourist guides',         path: '/tourism',   color: 'saffron'},
              { icon: '🏭', label: 'Business',        desc: 'Factory products, websites, tech solutions',     path: '/services',  color: 'teal'   },
            ].map((cat, i) => (
              <button
                key={cat.label}
                className={`home-cat-card home-cat-card--${cat.color} animate-fade-up delay-${i + 1}`}
                onClick={() => navigate(cat.path)}
              >
                <div className="home-cat-card__icon">{cat.icon}</div>
                <h3 className="home-cat-card__title">{cat.label}</h3>
                <p className="home-cat-card__desc">{cat.desc}</p>
                <span className="home-cat-card__arrow">→</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────── */}
      <section className="section-pad home-testimonials">
        <div className="container">
          <SectionHeader
            badge="Client Stories"
            badgeVariant="gold"
            title="Trusted by Clients from Around the World"
            subtitle="Hear from international visitors, students, and business clients who've used our services in Mumbai bombay international worldwide."
          />
          <div className="home-testimonials__grid">
            {TESTIMONIALS.map(t => (
              <TestimonialCard key={t.id} testimonial={t} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <CTABanner
        title="Ready to Get Started? Send Us an Inquiry."
        sub="Tell us what you need in Mumbai bombay international worldwide - our team responds within 2–4 hours."
      />
    </div>
  );
}